import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const FilterBar = ({ filters, onFilterChange, onSaveFilter, onExport, onSearch }) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || '');
  const [searchBy, setSearchBy] = useState('all');

  const propertyTypeOptions = [
    { value: 'all', label: 'جميع الأنواع' },
    { value: 'شقة', label: 'شقة' },
    { value: 'فيلا', label: 'فيلا' },
    { value: 'مكتب', label: 'مكتب' },
    { value: 'محل', label: 'محل' },
    { value: 'أرض', label: 'أرض' }
  ];

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'متاح', label: 'متاح' },
    { value: 'محجوز', label: 'محجوز' },
    { value: 'مؤجر', label: 'مؤجر' },
    { value: 'صيانة', label: 'صيانة' },
    { value: 'معلق', label: 'معلق' }
  ];

  const searchByOptions = [
    { value: 'all', label: 'بحث شامل' },
    { value: 'propertyId', label: 'رقم العقار' },
    { value: 'address', label: 'العنوان' },
    { value: 'owner', label: 'اسم المالك' },
    { value: 'ownerPhone', label: 'رقم المالك' },
    { value: 'district', label: 'المنطقة' }
  ];

  const handleSearchClick = () => {
    if (onSearch && typeof onSearch === 'function') {
      onSearch(searchTerm, searchBy);
    } else {
      onFilterChange('search', searchTerm);
      onFilterChange('searchBy', searchBy);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSearchBy('all');
    onFilterChange('search', '');
    onFilterChange('searchBy', 'all');
    onFilterChange('propertyType', 'all');
    onFilterChange('status', 'all');
    onFilterChange('dateFrom', '');
    onFilterChange('dateTo', '');
  };

  const hasActiveFilters = () => {
    return (
      searchTerm ||
      (filters?.propertyType && filters?.propertyType !== 'all') ||
      (filters?.status && filters?.status !== 'all') ||
      filters?.dateFrom ||
      filters?.dateTo
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 shadow-warm space-y-4" style={{
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF8E7 100%)',
      borderColor: 'rgba(184, 134, 11, 0.2)'
    }}>
      {/* Search Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Search" size={18} color="#B8860B" />
          <h3 className="text-sm font-semibold text-foreground">البحث والفلاتر</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <Select
              label="البحث حسب"
              options={searchByOptions}
              value={searchBy}
              onChange={(value) => setSearchBy(value)}
            />
          </div>
          
          <div className="md:col-span-7">
            <Input
              type="text"
              label="كلمة البحث"
              placeholder="ابحث عن عقار، مالك، رقم، عنوان..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              onKeyPress={(e) => {
                if (e?.key === 'Enter') {
                  handleSearchClick();
                }
              }}
            />
          </div>
          
          <div className="md:col-span-2 flex items-end">
            <Button
              variant="default"
              fullWidth
              iconName="Search"
              iconPosition="right"
              onClick={handleSearchClick}
              className="w-full"
              style={{
                background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
                color: '#FFFFFF',
                minHeight: '42px'
              }}
            >
              بحث
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <Select
          label="نوع العقار"
          options={propertyTypeOptions}
          value={filters?.propertyType}
          onChange={(value) => onFilterChange('propertyType', value)}
        />

        <Select
          label="الحالة"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => onFilterChange('status', value)}
        />

        <Input
          type="date"
          label="من تاريخ"
          value={filters?.dateFrom}
          onChange={(e) => onFilterChange('dateFrom', e?.target?.value)}
        />

        <Input
          type="date"
          label="إلى تاريخ"
          value={filters?.dateTo}
          onChange={(e) => onFilterChange('dateTo', e?.target?.value)}
        />

        <div className="flex items-end gap-2">
          <Button
            variant="outline"
            fullWidth
            iconName="X"
            iconPosition="right"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters()}
            style={{
              borderColor: '#B8860B',
              color: '#B8860B',
              opacity: hasActiveFilters() ? 1 : 0.5
            }}
          >
            مسح
          </Button>
          {onExport && (
            <Button
              variant="outline"
              size="icon"
              iconName="Download"
              onClick={onExport}
              style={{
                borderColor: '#B8860B',
                color: '#B8860B'
              }}
            />
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
          <span className="text-xs text-muted-foreground">الفلاتر النشطة:</span>
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-primary/10 text-primary border border-primary/20">
              <Icon name="Search" size={12} />
              {searchTerm}
            </span>
          )}
          {filters?.propertyType && filters?.propertyType !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-primary/10 text-primary border border-primary/20">
              <Icon name="Building2" size={12} />
              {propertyTypeOptions?.find(opt => opt?.value === filters?.propertyType)?.label}
            </span>
          )}
          {filters?.status && filters?.status !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-primary/10 text-primary border border-primary/20">
              <Icon name="CheckCircle" size={12} />
              {statusOptions?.find(opt => opt?.value === filters?.status)?.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;